import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { useBdNotes, useSaveBdNotes } from '../../../hooks/useBasicDesign.js';
import { formatLocalYmdHm } from '../../../lib/date-utils.js';
import { TabButton, TabGroup } from '../../ui/Tabs/index.js';
import { WikiMarkdown } from '../knowledge-wiki/WikiMarkdown.js';
import { BdApiErrorText } from './BdApiErrorText.js';
import type { BdNotes } from '../../../lib/api/basic-design.js';

export interface BdNotesPanelProps {
  /** 基本設計のチケット */
  bdKey: string;
}

/** 編集中の見せ方。 @graph-connects none */
type NotesMode = 'write' | 'preview';

/**
 * 基本設計の共有のメモ。節に入らない前提・経緯・相談したことを Markdown で自由に書く。
 *
 * メモは基本設計と一緒に保存され、レビュアーも読めるが、進み具合にもレビューにも数えない
 * （決まった形で書くものは節に書く）。書けるのは節と同じ担当者で、判定と上限は API が持つ。
 * 読むときは wiki 本文と同じ `WikiMarkdown` で描き、記法の扱いを画面ごとに書き分けない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の共有のメモを Markdown で読み、書いてプレビューし、保存する
 * @graph-connects useBdNotes [calls] メモの取得
 * @graph-connects WikiMarkdown [calls] Markdown の描画
 * @graph-connects BdNotesEditor [calls] メモの編集
 */
export function BdNotesPanel({ bdKey }: BdNotesPanelProps) {
  const { t } = useTranslation();
  const notes = useBdNotes(bdKey, true);
  const [editing, setEditing] = useState(false);

  return (
    <section className="space-y-3" aria-label={t('bd.notes.title')}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
            {t('bd.notes.title')}
          </h2>
          <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">{t('bd.notes.lead')}</p>
        </div>
        {editing || notes.data === undefined ? null : (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setEditing(true);
            }}
          >
            {t('bd.section.edit')}
          </Button>
        )}
      </div>

      {notes.error !== null ? (
        <BdApiErrorText error={notes.error} />
      ) : notes.data === undefined ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t('bd.loading')}</p>
      ) : editing ? (
        <BdNotesEditor
          bdKey={bdKey}
          notes={notes.data}
          onDone={() => {
            setEditing(false);
          }}
        />
      ) : (
        <NotesView notes={notes.data} />
      )}
    </section>
  );
}

/** 保存済みのメモ。空なら案内を出す。 @graph-connects WikiMarkdown [calls] Markdown の描画 */
function NotesView({ notes }: { notes: BdNotes }) {
  const { t } = useTranslation();
  if (notes.markdown.trim() === '') {
    return (
      <div className="rounded-xl border border-dashed border-[var(--color-border-hover)] px-6 py-8 text-center text-sm text-[var(--color-text-secondary)]">
        {t('bd.notes.empty')}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-[var(--color-border)] px-4 py-3 text-sm">
        <WikiMarkdown markdown={notes.markdown} />
      </div>
      {notes.updatedAt === null ? null : (
        <p className="text-xs text-[var(--color-text-muted)]">
          {t('bd.notes.meta', {
            version: notes.version,
            date: formatLocalYmdHm(notes.updatedAt),
            who: notes.updatedBy ?? '',
          })}
        </p>
      )}
    </div>
  );
}

interface BdNotesEditorProps {
  bdKey: string;
  notes: BdNotes;
  onDone: () => void;
}

/**
 * メモの編集。書く・プレビューを切り替えて、保存する。
 * @graph-connects useSaveBdNotes [calls] メモの保存
 */
function BdNotesEditor({ bdKey, notes, onDone }: BdNotesEditorProps) {
  const { t } = useTranslation();
  const save = useSaveBdNotes(bdKey);
  const [markdown, setMarkdown] = useState(notes.markdown);
  const [mode, setMode] = useState<NotesMode>('write');

  return (
    <div className="space-y-3">
      <TabGroup ariaLabel={t('bd.notes.modeAria')}>
        {(['write', 'preview'] as const).map((m) => (
          <TabButton
            key={m}
            active={mode === m}
            label={t(`bd.notes.mode.${m}`)}
            onClick={() => {
              setMode(m);
            }}
          />
        ))}
      </TabGroup>
      {mode === 'write' ? (
        <Textarea
          aria-label={t('bd.notes.title')}
          rows={14}
          className="font-mono"
          value={markdown}
          onChange={(e) => {
            setMarkdown(e.target.value);
          }}
        />
      ) : markdown.trim() === '' ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t('bd.notes.previewEmpty')}</p>
      ) : (
        <div className="rounded-lg border border-[var(--color-border)] px-4 py-3 text-sm">
          <WikiMarkdown markdown={markdown} />
        </div>
      )}
      <p className="text-xs text-[var(--color-text-muted)]">
        {t('bd.notes.length', { n: markdown.length })}
      </p>
      {save.error === null ? null : <BdApiErrorText error={save.error} />}
      <div className="flex gap-2">
        <Button
          size="sm"
          loading={save.isPending}
          disabled={save.isPending}
          onClick={() => {
            save.mutate(markdown, { onSuccess: onDone });
          }}
        >
          {save.isPending ? t('bd.editor.saving') : t('bd.editor.save')}
        </Button>
        <Button size="sm" variant="ghost" disabled={save.isPending} onClick={onDone}>
          {t('bd.editor.cancel')}
        </Button>
      </div>
    </div>
  );
}
